// Copyright (c) Ping. All rights reserved.
//
// Smart anchored popover (web). Positions itself relative to a trigger element,
// flips up/down based on available space, clamps horizontally to the viewport,
// and renders in a portal so it's never clipped by a fixed surface or overflow.
// Closes on outside-click + Escape. Re-measures on resize/scroll and when its
// own content resizes. The canonical app-wide popover primitive — native sibling
// (`AnchoredPopover.tsx`) is a Modal-backed equivalent.
//
// `anchorRef` accepts an RN-web View/Pressable ref (its `.current` is the host
// DOM node) or a raw DOM element ref.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type AnyRef = { current: unknown };

export interface AnchoredPopoverProps {
  readonly anchorRef: AnyRef;
  readonly open: boolean;
  readonly onClose: () => void;
  /** Preferred side relative to the trigger. 'auto' picks the side with room. */
  readonly placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  /** Horizontal alignment to the trigger. */
  readonly align?: 'center' | 'start' | 'end';
  readonly gap?: number;
  /**
   * Keep keyboard focus inside the panel while open. Needed when the popover
   * carries a focusable control (e.g. a search `TextInput`) AND is rendered
   * under an ancestor RN `Modal` — RNW's `ModalFocusTrap` otherwise yanks focus
   * out of the body-portaled panel, making the field untypable. Registered on
   * open (after the ancestor trap) so it gets the last word and routes focus
   * back into the panel. No-op on native (the native sibling is `Modal`-backed
   * and already traps focus).
   */
  readonly trapFocus?: boolean;
  readonly children: React.ReactNode;
}

const MARGIN = 8;

function rectOf(ref: AnyRef): DOMRect | null {
  const node = ref.current as { getBoundingClientRect?: () => DOMRect } | null;
  if (!node || typeof node.getBoundingClientRect !== 'function') return null;
  return node.getBoundingClientRect();
}

export function AnchoredPopover({
  anchorRef,
  open,
  onClose,
  placement = 'top',
  align = 'center',
  gap = 8,
  trapFocus = false,
  children,
}: AnchoredPopoverProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; ready: boolean }>({
    top: 0,
    left: 0,
    ready: false,
  });

  const reposition = useCallback(() => {
    const a = rectOf(anchorRef);
    const panel = panelRef.current;
    if (!a || !panel) return;
    const pw = panel.offsetWidth;
    const ph = panel.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const clamp = (v: number, max: number) => Math.min(Math.max(v, MARGIN), Math.max(MARGIN, max));

    // Cross-axis positions, aligned to the trigger then clamped on-screen.
    // For top/bottom the cross-axis is horizontal (cx); for left/right it's
    // vertical (cy). `align` ('start'|'center'|'end') drives both.
    const cx =
      align === 'start' ? a.left : align === 'end' ? a.right - pw : a.left + a.width / 2 - pw / 2;
    const cy =
      align === 'start' ? a.top : align === 'end' ? a.bottom - ph : a.top + a.height / 2 - ph / 2;
    const centerX = clamp(cx, vw - pw - MARGIN);
    const centerY = clamp(cy, vh - ph - MARGIN);

    const spaceAbove = a.top;
    const spaceBelow = vh - a.bottom;
    const spaceLeft = a.left;
    const spaceRight = vw - a.right;

    // Each candidate: does the panel fit on that side, where it lands, and how
    // much free space that side offers (for the no-fit fallback).
    type C = { fits: boolean; free: number; top: number; left: number };
    const top: C = { fits: spaceAbove >= ph + gap, free: spaceAbove, top: a.top - gap - ph, left: centerX };
    const bottom: C = { fits: spaceBelow >= ph + gap, free: spaceBelow, top: a.bottom + gap, left: centerX };
    const left: C = { fits: spaceLeft >= pw + gap, free: spaceLeft, top: centerY, left: a.left - gap - pw };
    const right: C = { fits: spaceRight >= pw + gap, free: spaceRight, top: centerY, left: a.right + gap };

    // Preference order by requested side, then sensible fallbacks.
    let order: C[];
    if (placement === 'right') order = [right, left, bottom, top];
    else if (placement === 'left') order = [left, right, bottom, top];
    else {
      const vertical = placement === 'bottom' ? [bottom, top] : [top, bottom];
      const horizontal = spaceLeft >= spaceRight ? [left, right] : [right, left];
      order = [...vertical, ...horizontal];
    }

    const chosen =
      order.find((c) => c.fits) ?? order.reduce((best, c) => (c.free > best.free ? c : best), order[0]!);

    setPos({
      top: clamp(chosen.top, vh - ph - MARGIN),
      left: clamp(chosen.left, vw - pw - MARGIN),
      ready: true,
    });
  }, [anchorRef, placement, align, gap]);

  // Measure before paint when opened, then keep in sync.
  useLayoutEffect(() => {
    if (!open) {
      setPos((p) => (p.ready ? { ...p, ready: false } : p));
      return;
    }
    reposition();
    const ro = new ResizeObserver(reposition);
    if (panelRef.current) ro.observe(panelRef.current);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, reposition]);

  // Outside-click + Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      // Clicks inside ANY anchored-popover portal (e.g. a nested pin/unpin
      // menu rendered to body) must not close this one.
      const tEl = t as Element;
      if (typeof tEl?.closest === 'function' && tEl.closest('[data-anchored-popover]')) return;
      const anchor = anchorRef.current as Node | null;
      if (anchor && 'contains' in anchor && (anchor as Node).contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // pointerdown (capture) so it fires before the trigger's onPress re-toggles.
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, anchorRef]);

  // Optional focus containment — see `trapFocus` prop. Routes focus back into
  // the panel when it lands outside, defeating an ancestor Modal's focus trap.
  useEffect(() => {
    if (!open || !trapFocus || typeof document === 'undefined') return;
    let inProgress = false;
    const focusInside = (): void => {
      const panel = panelRef.current;
      if (!panel) return;
      const el =
        panel.querySelector<HTMLElement>('input,textarea,select') ??
        panel.querySelector<HTMLElement>(
          'button,[href],[tabindex]:not([tabindex="-1"])',
        ) ??
        panel;
      el.focus?.();
    };
    const onFocus = (e: FocusEvent): void => {
      if (inProgress) return;
      const panel = panelRef.current;
      if (!panel) return;
      const t = e.target as Node | null;
      if (t && panel.contains(t)) return; // already inside — leave it
      inProgress = true;
      try {
        focusInside();
      } finally {
        inProgress = false;
      }
    };
    // Pull focus in once after the panel mounts (the ancestor trap or autoFocus
    // race can otherwise leave focus outside).
    const id = window.setTimeout(focusInside, 0);
    document.addEventListener('focus', onFocus, true);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('focus', onFocus, true);
    };
  }, [open, trapFocus]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={panelRef}
      data-anchored-popover=""
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        zIndex: 2147483640,
        // Hidden until measured so it never flashes at 0,0.
        visibility: pos.ready ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
